import { useCallback, useEffect, useMemo, useState } from "react"
import { Link } from "@tanstack/react-router"
import { format } from "date-fns"
import {
	IconStackPerspectiveOutline18 as ChangelogIcon,
	IconCopyOutline18 as Copy,
	IconBookOpenOutline18 as DocsIcon,
	IconArrowDownToLineOutline18 as Download,
	IconMagnifierOutline18 as Search,
	IconChatBubbleOutline18 as ThreadsIcon,
	IconTriangleWarningOutline18 as WarningIcon,
} from "nucleo-ui-outline-18"

import {
	Avatar,
	Button,
	IconButton,
	Input,
	LinearIcon,
	Loader,
	ScrollArea,
	SlackIcon,
	SortableTableHeader,
	Tooltip,
	Virtualizer,
} from "@productlane/ui"
import { EmailIcon } from "@productlane/ui/src/icons/project/email-icon"
import { WorldIcon } from "@productlane/ui/src/icons/project/world-icon"

import type { CustomerFilters } from "./customers-filter"
import { currentPath } from "@/components/impersonation/impersonation-context"
import { toast } from "@/components/toast"
import { api } from "@/lib/api"
import {
	applyCustomerFilters,
	CustomersFilterChips,
	CustomersFilterPopover,
	EMPTY_CUSTOMER_FILTERS,
} from "./customers-filter"
import { WorkspaceMembersMenu } from "./workspace-members-menu"
import { WorkspaceOptionsMenu } from "./workspace-options-menu"
import { filterWorkspacesBySearch } from "./workspace-search-match"
import { WorkspaceStripeMenu } from "./workspace-stripe-menu"

type SortType =
	| "newest"
	| "oldest"
	| "name-asc"
	| "name-desc"
	| "mrr-asc"
	| "mrr-desc"
	| "members-asc"
	| "members-desc"
	| "contacts-asc"
	| "contacts-desc"
	| "activation-asc"
	| "activation-desc"
	| "tier-asc"
	| "tier-desc"
	| "stage-asc"
	| "stage-desc"
	| "owner-asc"
	| "owner-desc"
	| "resolution-asc"
	| "resolution-desc"
	| "lastthread-asc"
	| "lastthread-desc"

const DIRECTORY_STALE_TIME = 60 * 1000

const stageRank = (w: {
	stripeSubscriptionId: string | null
	hasBeenSetup: boolean
}) => (w.stripeSubscriptionId ? 2 : w.hasBeenSetup ? 1 : 0)

const activationScore = (w: {
	hasCustomDomain: boolean
	emailConnected: boolean
	slackConnected: boolean
	linearConnected: boolean
}) =>
	Number(w.hasCustomDomain) +
	Number(w.emailConnected) +
	Number(w.slackConnected) +
	Number(w.linearConnected)

const formatMrr = (mrr: number | null) =>
	mrr === null ? "" : `€${Math.round(mrr).toLocaleString("de-DE")}`

export function AdminCustomersTable({
	selectedFeatures,
	onSelectedFeaturesChange,
}: {
	selectedFeatures: string[]
	onSelectedFeaturesChange: (features: string[]) => void
}) {
	const [search, setSearch] = useState("")
	const [debouncedSearch, setDebouncedSearch] = useState("")
	const [sortType, setSortType] = useState<SortType>("newest")
	const utils = api.useUtils()
	const impersonateMutation = api.user.impersonateUser.useMutation({
		onSuccess: async () => {
			await utils.user.info.reset()
			if (typeof window !== "undefined") {
				window.location.replace("/threads")
			}
		},
	})

	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearch(search)
		}, 300)
		return () => clearTimeout(timer)
	}, [search])

	const workspacesQuery = api.analytics.listWorkspaces.useQuery(
		{},
		{ staleTime: DIRECTORY_STALE_TIME },
	)

	const enrichmentQuery = api.analytics.listWorkspacesEnrichment.useQuery(
		{},
		{ staleTime: DIRECTORY_STALE_TIME },
	)

	const trimmedSearch = debouncedSearch.trim()

	// The browser matches name, domain and owner email on its own; this only
	// adds the workspaces whose member emails match.
	const searchIdsQuery = api.analytics.searchWorkspaceIds.useQuery(
		{ search: trimmedSearch },
		{ enabled: trimmedSearch.length > 0, staleTime: 60000 },
	)

	const serverMatchIds = useMemo(() => {
		if (!trimmedSearch || !searchIdsQuery.data) return null
		return new Set(searchIdsQuery.data.ids)
	}, [trimmedSearch, searchIdsQuery.data])

	const enrichmentById = useMemo(() => {
		const map = new Map<
			string,
			{
				mrr: number | null
				companyCount: number
				trialDaysLeft: number | null
				lastAiResolutionAt: Date | null
			}
		>()
		for (const e of enrichmentQuery.data ?? []) {
			map.set(e.id, {
				mrr: e.mrr,
				companyCount: e.companyCount,
				trialDaysLeft: e.trialDaysLeft ?? null,
				lastAiResolutionAt: e.lastAiResolutionAt ?? null,
			})
		}
		return map
	}, [enrichmentQuery.data])

	const allItems = useMemo(() => {
		const items = workspacesQuery.data?.items ?? []
		return items.map((w) => {
			const e = enrichmentById.get(w.id)
			return {
				...w,
				mrr: e?.mrr ?? null,
				companyCount: e?.companyCount ?? 0,
				trialDaysLeft: e?.trialDaysLeft ?? null,
				lastAiResolutionAt: e?.lastAiResolutionAt ?? null,
			}
		})
	}, [workspacesQuery.data, enrichmentById])

	const enrichedItems = useMemo(
		() => filterWorkspacesBySearch(allItems, trimmedSearch, serverMatchIds),
		[allItems, trimmedSearch, serverMatchIds],
	)

	const [localFilters, setLocalFilters] = useState<CustomerFilters>(
		EMPTY_CUSTOMER_FILTERS,
	)
	const filters = useMemo<CustomerFilters>(
		() => ({ ...localFilters, features: selectedFeatures }),
		[localFilters, selectedFeatures],
	)
	const setFilters = useCallback(
		(next: CustomerFilters) => {
			onSelectedFeaturesChange(next.features)
			setLocalFilters(next)
		},
		[onSelectedFeaturesChange],
	)

	const filteredItems = useMemo(
		() => applyCustomerFilters(enrichedItems, filters),
		[enrichedItems, filters],
	)

	const sortedAll = useMemo(() => {
		const copy = [...filteredItems]
		copy.sort((a, b) => {
			switch (sortType) {
				case "newest":
					return (
						new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
					)
				case "oldest":
					return (
						new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
					)
				case "name-asc":
					return (a.name || "").localeCompare(b.name || "")
				case "name-desc":
					return (b.name || "").localeCompare(a.name || "")
				case "mrr-asc":
					return (a.mrr ?? -1) - (b.mrr ?? -1)
				case "mrr-desc":
					return (b.mrr ?? -1) - (a.mrr ?? -1)
				case "members-asc":
					return a.memberCount - b.memberCount
				case "members-desc":
					return b.memberCount - a.memberCount
				case "contacts-asc":
					return a.contactCount - b.contactCount
				case "contacts-desc":
					return b.contactCount - a.contactCount
				case "activation-asc":
					return activationScore(a) - activationScore(b)
				case "activation-desc":
					return activationScore(b) - activationScore(a)
				case "tier-asc":
					return Number(!!a.stripePriceId) - Number(!!b.stripePriceId)
				case "tier-desc":
					return Number(!!b.stripePriceId) - Number(!!a.stripePriceId)
				case "stage-asc":
					return stageRank(a) - stageRank(b)
				case "stage-desc":
					return stageRank(b) - stageRank(a)
				case "owner-asc":
					return (a.createdBy?.email ?? "").localeCompare(
						b.createdBy?.email ?? "",
					)
				case "owner-desc":
					return (b.createdBy?.email ?? "").localeCompare(
						a.createdBy?.email ?? "",
					)
				case "resolution-asc":
					return (
						(a.lastAiResolutionAt
							? new Date(a.lastAiResolutionAt).getTime()
							: 0) -
						(b.lastAiResolutionAt
							? new Date(b.lastAiResolutionAt).getTime()
							: 0)
					)
				case "resolution-desc":
					return (
						(b.lastAiResolutionAt
							? new Date(b.lastAiResolutionAt).getTime()
							: 0) -
						(a.lastAiResolutionAt
							? new Date(a.lastAiResolutionAt).getTime()
							: 0)
					)
				case "lastthread-asc":
					return (
						(a.lastThreadAt ? new Date(a.lastThreadAt).getTime() : 0) -
						(b.lastThreadAt ? new Date(b.lastThreadAt).getTime() : 0)
					)
				case "lastthread-desc":
					return (
						(b.lastThreadAt ? new Date(b.lastThreadAt).getTime() : 0) -
						(a.lastThreadAt ? new Date(a.lastThreadAt).getTime() : 0)
					)
				default:
					return 0
			}
		})
		return copy
	}, [filteredItems, sortType])

	const total = sortedAll.length
	const sorted = sortedAll

	const handleDownload = useCallback(() => {
		const escape = (value: string) => {
			if (/[",\n]/.test(value)) {
				return `"${value.replace(/"/g, '""')}"`
			}
			return value
		}
		const header = [
			"Email",
			"Name",
			"Workspace",
			"Created",
			"Tier",
			"Stage",
			"MRR",
			"Last AI resolution",
			"Features",
		].join(",")
		const rows = sortedAll.map((w) => {
			const stage = w.stripeSubscriptionId
				? "Active"
				: w.hasBeenSetup
					? "Free"
					: "New"
			return [
				w.createdBy?.email ?? "",
				w.createdBy?.name ?? "",
				w.name ?? w.domain,
				format(new Date(w.createdAt), "yyyy-MM-dd"),
				w.stripePriceId ? "Paid" : "",
				stage,
				w.mrr === null ? "" : String(Math.round(w.mrr)),
				w.lastAiResolutionAt
					? format(new Date(w.lastAiResolutionAt), "yyyy-MM-dd")
					: "",
				w.features.join(" | "),
			]
				.map((v) => escape(v))
				.join(",")
		})
		const csv = [header, ...rows].join("\n")
		const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
		const url = URL.createObjectURL(blob)
		const link = document.createElement("a")
		link.href = url
		link.download = `workspaces-${format(new Date(), "yyyy-MM-dd")}.csv`
		document.body.appendChild(link)
		link.click()
		document.body.removeChild(link)
		URL.revokeObjectURL(url)
	}, [sortedAll])

	return (
		<div className="flex h-[calc(100dvh-6.75rem)] w-full min-w-0 flex-col overflow-hidden">
			<div className="flex items-center justify-between gap-4 border-b border-gray-200 px-6 py-2 dark:border-gray-800">
				<div className="-mt-px flex w-full items-center gap-2">
					<Search className="h-4 w-4 flex-none text-secondary" />
					<Input
						variant="borderless"
						textSize="sm"
						placeholder="Find by name, domain, email, or workspace ID..."
						value={search}
						name="workspace-search"
						className="px-0 placeholder:text-tertiary"
						inputClassName="h-7"
						onChange={(e) => {
							setSearch(e.target.value)
						}}
					/>
				</div>
				<div className="flex items-center gap-3">
					<div className="whitespace-nowrap text-xs text-tertiary">
						{workspacesQuery.data ? `${total.toLocaleString()} workspaces` : ""}
					</div>
					<CustomersFilterPopover
						filters={filters}
						onChange={setFilters}
						rows={enrichedItems}
					/>
					<Button
						variant="outline"
						Icon={Download}
						disabled={!workspacesQuery.data || sorted.length === 0}
						onClick={handleDownload}
					>
						Download CSV
					</Button>
				</div>
			</div>

			<CustomersFilterChips
				filters={filters}
				onChange={setFilters}
				rows={enrichedItems}
			/>

			<div className="flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-x-auto">
				<div className="flex min-h-0 min-w-[1900px] flex-1 flex-col">
					<div className="sticky top-0 z-1 flex items-center justify-between border-b bg-gray-50/80 px-6 py-2 text-xs text-secondary backdrop-blur-md dark:bg-gray-900">
						<div className="w-64 flex-none">
							<SortableTableHeader
								label="Name"
								currentSortType={sortType}
								sortTypeAsc="name-asc"
								sortTypeDesc="name-desc"
								onClick={setSortType}
							/>
						</div>
						<div className="flex items-center gap-4">
							<div className="w-20 flex-none">
								<SortableTableHeader
									label="MRR"
									currentSortType={sortType}
									sortTypeAsc="mrr-asc"
									sortTypeDesc="mrr-desc"
									onClick={setSortType}
									arrowPosition="left"
								/>
							</div>
							<div className="w-[85px] flex-none">
								<SortableTableHeader
									label="Activation"
									currentSortType={sortType}
									sortTypeAsc="activation-asc"
									sortTypeDesc="activation-desc"
									onClick={setSortType}
									arrowPosition="left"
									className="ml-auto"
								/>
							</div>
							<div className="w-10 flex-none">
								<SortableTableHeader
									label="Size"
									currentSortType={sortType}
									sortTypeAsc="members-asc"
									sortTypeDesc="members-desc"
									onClick={setSortType}
									arrowPosition="left"
								/>
							</div>
							<div className="w-24 flex-none">
								<SortableTableHeader
									label="Tier"
									currentSortType={sortType}
									sortTypeAsc="tier-asc"
									sortTypeDesc="tier-desc"
									onClick={setSortType}
									arrowPosition="left"
								/>
							</div>
							<div className="w-24 flex-none">
								<SortableTableHeader
									label="Stage"
									currentSortType={sortType}
									sortTypeAsc="stage-asc"
									sortTypeDesc="stage-desc"
									onClick={setSortType}
									arrowPosition="left"
								/>
							</div>
							<div className="w-20 flex-none">
								<SortableTableHeader
									label="Contacts"
									currentSortType={sortType}
									sortTypeAsc="contacts-asc"
									sortTypeDesc="contacts-desc"
									onClick={setSortType}
									arrowPosition="left"
								/>
							</div>
							<div className="w-24 flex-none">
								<SortableTableHeader
									label="Created"
									currentSortType={sortType}
									sortTypeAsc="oldest"
									sortTypeDesc="newest"
									onClick={setSortType}
								/>
							</div>
							<div className="w-28 flex-none">
								<SortableTableHeader
									label="Last AI"
									currentSortType={sortType}
									sortTypeAsc="resolution-asc"
									sortTypeDesc="resolution-desc"
									onClick={setSortType}
								/>
							</div>
							<div className="w-36 flex-none">Usage</div>
							<div className="w-32 flex-none">
								<SortableTableHeader
									label="Last thread"
									currentSortType={sortType}
									sortTypeAsc="lastthread-asc"
									sortTypeDesc="lastthread-desc"
									onClick={setSortType}
								/>
							</div>
							<div className="w-56 flex-none">
								<SortableTableHeader
									label="Owner"
									currentSortType={sortType}
									sortTypeAsc="owner-asc"
									sortTypeDesc="owner-desc"
									onClick={setSortType}
									className="flex w-full"
								/>
							</div>
							<div className="w-40 flex-none" />
						</div>
					</div>

					{workspacesQuery.isLoading ||
					(sorted.length === 0 && searchIdsQuery.isFetching) ? (
						<div className="flex flex-1 items-center justify-center">
							<Loader />
						</div>
					) : sorted.length > 0 ? (
						<ScrollArea>
							<Virtualizer
								className="h-[calc(100dvh-15.75rem)] pb-32 text-sm lg:pb-20"
								itemsData={sorted}
								itemContent={(workspace) => {
									return (
										<div className="flex select-none items-center justify-between px-6 py-2.5 hover:bg-gray-100 dark:hover:bg-gray-850/50">
											<div className="flex w-64 flex-none items-center">
												<Avatar
													size="s"
													name={workspace.name || workspace.domain}
													image={workspace.logoUrl}
													className="mr-4"
												/>
												<Link
													to="/iluvatar/workspaces/$id"
													params={{ id: workspace.id }}
													className="truncate text-sm font-medium leading-tight hover:underline"
												>
													{workspace.name || workspace.domain}
												</Link>
											</div>
											<div className="flex items-center gap-4">
												<div className="w-20 flex-none text-right">
													{enrichmentQuery.data ? (
														formatMrr(workspace.mrr)
													) : workspace.stripeSubscriptionId ? (
														<span className="inline-block h-3 w-10 animate-pulse rounded bg-gray-100 dark:bg-gray-800" />
													) : (
														""
													)}
												</div>
												<div className="flex w-[85px] flex-none items-center justify-end gap-1">
													{workspace.hasCustomDomain ? (
														<Tooltip
															label={workspace.customDomain ?? ""}
															shouldWrapChildren
														>
															<WorldIcon className="h-4 w-4 text-blue-500" />
														</Tooltip>
													) : null}
													{workspace.emailConnected ? (
														<Tooltip
															label={workspace.emailAddress ?? "Connected"}
															shouldWrapChildren
														>
															<EmailIcon className="h-4 w-4 text-green-500" />
														</Tooltip>
													) : null}
													{workspace.slackConnected ? (
														<Tooltip label="Slack connected" shouldWrapChildren>
															<SlackIcon className="h-4 w-4" />
														</Tooltip>
													) : null}
													{workspace.linearConnected ? (
														<Tooltip
															label="Linear connected"
															shouldWrapChildren
														>
															<LinearIcon className="h-4 w-4 text-black dark:text-white" />
														</Tooltip>
													) : null}
												</div>
												<div className="w-10 flex-none text-right">
													{workspace.memberCount}
												</div>
												<div className="w-24 flex-none truncate text-right text-tertiary">
													{workspace.stripePriceId ? "Paid" : ""}
												</div>
												<div className="w-24 flex-none text-right">
													{workspace.stripeSubscriptionId ? (
														<div className="flex items-center justify-end">
															<div className="mr-2 h-2 w-2 bg-green-500" />
															Active
														</div>
													) : workspace.hasBeenSetup ? (
														<div className="flex items-center justify-end">
															<div className="mr-2 h-2 w-2 bg-gray-400" />
															Free
														</div>
													) : (
														<div className="flex items-center justify-end">
															<div className="mr-2 h-2 w-2 bg-gray-300" />
															New
														</div>
													)}
												</div>
												<div className="flex w-20 flex-none items-center justify-end gap-2">
													<div className="rounded-[4px] bg-gray-100 px-[6px] py-[3px] text-xxs font-normal tabular-nums dark:bg-gray-800">
														{workspace.contactCount}
													</div>
												</div>
												<div className="w-24 flex-none text-left text-tertiary">
													{format(new Date(workspace.createdAt), "MMM d, yyyy")}
												</div>
												<div className="w-28 flex-none text-left text-tertiary">
													{enrichmentQuery.data ? (
														workspace.lastAiResolutionAt ? (
															format(
																new Date(workspace.lastAiResolutionAt),
																"MMM d, yyyy",
															)
														) : (
															<span className="text-gray-300 dark:text-gray-600">
																-
															</span>
														)
													) : (
														<span className="inline-block h-3 w-14 animate-pulse rounded bg-gray-100 dark:bg-gray-800" />
													)}
												</div>
												<div className="flex w-36 flex-none items-center gap-3 tabular-nums text-tertiary">
													<Tooltip label="Threads" shouldWrapChildren>
														<span className="flex items-center gap-1">
															<ThreadsIcon className="h-3.5 w-3.5" />
															{workspace.threadCount}
														</span>
													</Tooltip>
													<Tooltip label="Changelog posts" shouldWrapChildren>
														<span className="flex items-center gap-1">
															<ChangelogIcon className="h-3.5 w-3.5" />
															{workspace.changelogCount}
														</span>
													</Tooltip>
													<Tooltip
														label="Help center articles"
														shouldWrapChildren
													>
														<span className="flex items-center gap-1">
															<DocsIcon className="h-3.5 w-3.5" />
															{workspace.docCount}
														</span>
													</Tooltip>
												</div>
												<div className="flex w-32 flex-none items-center gap-1.5 text-left text-tertiary">
													{workspace.lastThreadAt ? (
														<>
															{Date.now() -
																new Date(workspace.lastThreadAt).getTime() >
															28 * 24 * 60 * 60 * 1000 ? (
																<Tooltip
																	label="No new thread in over 4 weeks"
																	shouldWrapChildren
																>
																	<WarningIcon className="h-3.5 w-3.5 text-yellow-500" />
																</Tooltip>
															) : null}
															{format(
																new Date(workspace.lastThreadAt),
																"MMM d, yyyy",
															)}
														</>
													) : (
														<span className="text-gray-300 dark:text-gray-600">
															-
														</span>
													)}
												</div>
												<div className="flex w-56 flex-none items-center gap-2 overflow-hidden">
													{workspace.createdBy ? (
														<>
															<Avatar
																size="xs"
																name={
																	workspace.createdBy.name ||
																	workspace.createdBy.email
																}
																image={workspace.createdBy.imageUrl}
															/>
															<div className="truncate text-left text-xs text-tertiary">
																{workspace.createdBy.email}
															</div>
														</>
													) : null}
												</div>
												<div className="flex w-40 flex-none items-center justify-end gap-1">
													<WorkspaceStripeMenu
														workspaceId={workspace.id}
														stripeCustomerId={workspace.stripeCustomerId}
														stripeSubscriptionId={
															workspace.stripeSubscriptionId
														}
														trialDaysLeft={workspace.trialDaysLeft}
													/>
													<WorkspaceMembersMenu
														workspaceId={workspace.id}
														disabled={impersonateMutation.isLoading}
														onImpersonate={(userId) => {
															impersonateMutation.mutate({
																userId,
																workspaceId: workspace.id,
																returnTo: currentPath(),
															})
														}}
													/>
													<Tooltip label="Copy workspace ID" shouldWrapChildren>
														<IconButton
															Icon={Copy}
															variant="ghost"
															onClick={async () => {
																await navigator.clipboard.writeText(
																	workspace.id,
																)
																toast.success({
																	title: "Workspace ID copied",
																})
															}}
														/>
													</Tooltip>
													<WorkspaceOptionsMenu
														workspaceId={workspace.id}
														domain={workspace.domain}
													/>
												</div>
											</div>
										</div>
									)
								}}
							/>
						</ScrollArea>
					) : (
						<div className="flex flex-1 items-center justify-center text-sm text-tertiary">
							No workspaces found
						</div>
					)}
				</div>
			</div>
		</div>
	)
}
